"use client";

import React from "react";
import { Mail, Phone, User, Users, ExternalLink, MoreVertical } from "lucide-react";
import { CompanyInfoCard } from "./CompanyInfoCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { useContactsPaginated } from "@/features/contacts/hooks";
import { useContactHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { TableRowActionsWrapper } from "@/components/shared/UniversalCRMTable/components/SimpleTable/TableRowActionsWrapper";
import { contactDetailActions } from "@/lib/actions/definitions/entities";
import { Skeleton } from "@/components/ui/skeleton";
import type { CompanyEntity } from "@/lib/types/company";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";

interface CompanyContactsQuickViewProps {
  company: CompanyEntity;
  onRefresh?: () => void;
}

/**
 * CompanyContactsQuickView Component
 * 
 * Shows a preview of top contacts at the company with quick actions.
 * Includes:
 * - Send email to all company contacts button
 * - List of up to 5 contacts with name, title, email, phone
 * - Action menu for each contact (email, call, create task, etc.)
 * - Link to full contacts tab
 */
export function CompanyContactsQuickView({ company, onRefresh }: CompanyContactsQuickViewProps) {
  const { openModal } = useGlobalModalStack();
  const contactHooks = useContactHooks();
  
  // Fetch contacts for this company (limit to 5 for preview)
  const {
    data: contactsData,
    isLoading,
    refetch: refetchContacts,
  } = useContactsPaginated({
    company_name: company.name || undefined,
    limit: 5,
    isActive: true,
  });

  const contacts = contactsData?.data || [];
  const totalCount = contactsData?.totalCount || 0;
  const hasMoreContacts = totalCount > 5;

  // Combined refetch handler
  const handleRefetch = async () => {
    await refetchContacts();
    onRefresh?.();
  };

  // Get email recipients for compose modal
  const emailRecipients = contacts
    .filter((contact) => contact.email && contact.email.trim())
    .map((contact) => ({
      id: contact.id,
      type: "client" as const,
      email: contact.email ?? "",
      name: contact.name,
      client: contact,
    }));

  const handleSendEmail = () => {
    openModal(MODAL_IDS.messages.compose, { 
      companyId: company.id,
      initialRecipients: emailRecipients,
    });
  };

  if (isLoading) {
    return (
      <CompanyInfoCard title="Key Contacts" icon={Users}>
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </CompanyInfoCard>
    );
  }

  if (contacts.length === 0) {
    return (
      <CompanyInfoCard title="Key Contacts" icon={Users}>
        <div className="text-sm text-gray-500 text-center py-4">
          No contacts found for this company
        </div>
      </CompanyInfoCard>
    );
  }

  return (
    <CompanyInfoCard 
      title="Key Contacts" 
      icon={Users}
      className="col-span-2"
    >
      {/* Header with Send Email button */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {totalCount} {totalCount === 1 ? "contact" : "contacts"} at this company
        </div>
        {emailRecipients.length > 0 && (
          <Button
            onClick={handleSendEmail}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <Mail className="h-4 w-4" />
            Send Email
          </Button>
        )}
      </div>

      {/* Contacts List */}
      <div className="space-y-3">
        {contacts.map((contact) => (
          <div
            key={contact.id}
            className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
          >
            <div className="flex-1 min-w-0">
              {/* Name and Title */}
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {contact.name}
                </span>
                {contact.title && (
                  <Badge variant="secondary" className="text-xs">
                    {contact.title}
                  </Badge>
                )}
              </div>

              {/* Contact Details */}
              <div className="flex flex-col gap-1 ml-6">
                {contact.email && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                    <Mail className="h-3 w-3 flex-shrink-0" />
                    <ContactMethodActions
                      kind="email"
                      value={contact.email}
                      entityContext={{
                        item: contact as unknown as Record<string, unknown>,
                        entityType: "contacts",
                      }}
                    />
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                    <Phone className="h-3 w-3 flex-shrink-0" />
                    <ContactMethodActions
                      kind="phone"
                      value={contact.phone}
                      entityContext={{
                        item: contact as unknown as Record<string, unknown>,
                        entityType: "contacts",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Actions Menu */}
            <div className="flex items-center gap-2 ml-3">
              {/* Quick View Link */}
              <a
                href={`/contacts/${contact.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  title="View contact details"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="sr-only">View contact details</span>
                </Button>
              </a>

              {/* Actions Dropdown */}
              <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <TableRowActionsWrapper
                  item={contact}
                  actions={contactDetailActions}
                  hooks={contactHooks}
                  entityName="contacts"
                  compact={true}
                  maxVisibleActions={0}
                  refetch={handleRefetch}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View All Link */}
      {hasMoreContacts && (
        <div className="mt-4 pt-4 border-t">
          <a
            href={`/companies/${company.id}?tab=contacts`}
            className="text-sm text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
          >
            View all {totalCount} contacts
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </CompanyInfoCard>
  );
}

